/**
 * Wallet Usage Context
 *
 * Global context for managing AI usage/transaction records with real-time Firestore sync.
 * Provides instant writes (no debouncing) and real-time updates via onSnapshot.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { DocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useMaybeOLVRate } from './OLVRateContext';
import type { Usage, UsageStatus, AIProvider } from '../types/walletFirestore';
import {
  createUsage,
  onUsageSnapshot,
  listUsage,
  CreateUsageInput,
} from '../repositories/walletUsageRepository';

// ============================================================================
// Types
// ============================================================================

interface WalletUsageState {
  usage: Usage[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  hasMore: boolean;
  version: number;
  latestUsage: Usage | null;
}

interface WalletUsageContextValue {
  state: WalletUsageState;
  /** Add a new usage record (instant write to Firestore) */
  addUsage: (input: AddUsageInput) => Promise<Usage | null>;
  /** Load more usage records (pagination) */
  loadMore: () => Promise<void>;
  /** Refresh usage from Firestore */
  refresh: () => void;
  /** Get a usage record by ID */
  getUsageById: (id: string) => Usage | undefined;
  /** Convert USD to OLV using current rate */
  usdToOlv: (usd: number) => number;
  /** Current OLV rate */
  olvRateUsd: number;
}

export interface AddUsageInput {
  /** Summary description (max 25 chars) */
  summary: string;
  /** Source page/feature */
  source: string;
  /** Amount in USD */
  amountUsd: number;
  /** AI model ID */
  modelId: string;
  /** Provider name */
  provider: AIProvider;
  /** Tags for categorization */
  tags?: string[];
  /** Initial status (defaults to 'successful') */
  status?: UsageStatus;
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 25;
const DEFAULT_OLV_RATE = 0.001;

// ============================================================================
// Reducer
// ============================================================================

type WalletUsageAction =
  | { type: 'SET_USAGE'; payload: { items: Usage[]; hasMore: boolean } }
  | { type: 'APPEND_USAGE'; payload: { items: Usage[]; hasMore: boolean } }
  | { type: 'ADD_USAGE'; payload: Usage }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR' };

const initialState: WalletUsageState = {
  usage: [],
  isLoading: true,
  error: null,
  totalCount: 0,
  hasMore: false,
  version: 0,
  latestUsage: null,
};

function walletUsageReducer(state: WalletUsageState, action: WalletUsageAction): WalletUsageState {
  switch (action.type) {
    case 'SET_USAGE':
      return {
        ...state,
        usage: action.payload.items,
        totalCount: action.payload.items.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        error: null,
        version: state.version + 1,
      };
    case 'APPEND_USAGE':
      const combined = [...state.usage, ...action.payload.items];
      return {
        ...state,
        usage: combined,
        totalCount: combined.length,
        hasMore: action.payload.hasMore,
        isLoading: false,
        version: state.version + 1,
      };
    case 'ADD_USAGE':
      const newUsage = [action.payload, ...state.usage];
      return {
        ...state,
        usage: newUsage,
        totalCount: state.totalCount + 1,
        version: state.version + 1,
        latestUsage: action.payload,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

const WalletUsageContext = createContext<WalletUsageContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function WalletUsageProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRate = useMaybeOLVRate();
  const [state, dispatch] = useReducer(walletUsageReducer, initialState);
  const cursorRef = useRef<DocumentSnapshot | null>(null);
  const unsubRef = useRef<Unsubscribe | null>(null);

  // Get effective OLV rate
  const olvRateUsd = olvRate?.getEffectiveRate() || DEFAULT_OLV_RATE;

  // Convert USD to OLV
  const usdToOlv = useCallback(
    (usd: number): number => {
      return usd / olvRateUsd;
    },
    [olvRateUsd]
  );

  // Set up real-time listener when user changes
  useEffect(() => {
    if (!user?.id) {
      dispatch({ type: 'CLEAR' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    // Start real-time sync
    unsubRef.current = onUsageSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_USAGE',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [user?.id]);

  // Add a new usage record (instant write)
  const addUsage = useCallback(
    async (input: AddUsageInput): Promise<Usage | null> => {
      if (!user?.id) return null;

      const rate = olvRateUsd;
      const memberId = user.profileId || user.id;

      const createInput: CreateUsageInput = {
        summary: input.summary,
        source: input.source,
        memberId,
        amountUsd: input.amountUsd,
        amountOlv: input.amountUsd / rate,
        olvRateAtTime: rate,
        modelId: input.modelId,
        provider: input.provider,
        tags: input.tags,
        status: input.status || 'successful',
      };

      try {
        const usage = await createUsage(user.id, createInput);
        // The real-time listener will update the state automatically
        // But we also add it optimistically for instant UI feedback
        dispatch({ type: 'ADD_USAGE', payload: usage });
        return usage;
      } catch (error) {
        console.error('[WalletUsageContext] Failed to create usage:', error);
        dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
        return null;
      }
    },
    [user?.id, user?.profileId, olvRateUsd]
  );

  // Load more usage records (pagination)
  const loadMore = useCallback(async () => {
    if (!user?.id || !cursorRef.current || state.isLoading) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const result = await listUsage(user.id, {
        limit: PAGE_SIZE,
        startAfter: cursorRef.current,
      });

      cursorRef.current = result.nextCursor;
      dispatch({
        type: 'APPEND_USAGE',
        payload: { items: result.items, hasMore: result.items.length >= PAGE_SIZE },
      });
    } catch (error) {
      console.error('[WalletUsageContext] Failed to load more usage:', error);
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
    }
  }, [user?.id, state.isLoading]);

  // Refresh usage
  const refresh = useCallback(() => {
    if (!user?.id) return;

    // Clean up and re-subscribe to trigger a fresh load
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    unsubRef.current = onUsageSnapshot(
      user.id,
      { limit: PAGE_SIZE },
      (items, nextCursor) => {
        cursorRef.current = nextCursor;
        dispatch({
          type: 'SET_USAGE',
          payload: { items, hasMore: items.length >= PAGE_SIZE },
        });
      },
      (error) => {
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    );
  }, [user?.id]);

  // Get usage by ID
  const getUsageById = useCallback(
    (id: string): Usage | undefined => {
      return state.usage.find((u) => u.id === id);
    },
    [state.usage]
  );

  const value: WalletUsageContextValue = {
    state,
    addUsage,
    loadMore,
    refresh,
    getUsageById,
    usdToOlv,
    olvRateUsd,
  };

  return (
    <WalletUsageContext.Provider value={value}>
      {children}
    </WalletUsageContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useWalletUsage(): WalletUsageContextValue {
  const context = useContext(WalletUsageContext);
  if (!context) {
    throw new Error('useWalletUsage must be used within a WalletUsageProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useMaybeWalletUsage(): WalletUsageContextValue | null {
  return useContext(WalletUsageContext);
}
